/**
 * PDF Uploader Component
 * 
 * Handles batch PDF file uploads with drag-and-drop support.
 */

import React, { useState, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';

const PdfUploader = ({ jobNumber, onUploadComplete, disabled }) => {
  const { instance, accounts } = useMsal();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const validateFiles = (fileList) => {
    const validFiles = [];
    const errors = [];

    for (const file of fileList) {
      if (file.type !== 'application/pdf') {
        errors.push(`${file.name}: Not a PDF file`);
        continue;
      }
      if (file.size > 100 * 1024 * 1024) { // 100MB limit
        errors.push(`${file.name}: File too large (max 100MB)`);
        continue;
      }
      validFiles.push(file);
    }

    if (errors.length > 0) {
      setError(errors.join('\n'));
    }

    return validFiles;
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setError(null);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const validFiles = validateFiles(Array.from(e.dataTransfer.files));
      setFiles(prev => [...prev, ...validFiles]);
    }
  }, []);

  const handleFileSelect = (e) => {
    setError(null);
    if (e.target.files && e.target.files.length > 0) {
      const validFiles = validateFiles(Array.from(e.target.files));
      setFiles(prev => [...prev, ...validFiles]);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0 || !jobNumber) {
      setError('Please select files and ensure a job number is set');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('jobNumber', jobNumber);
      
      files.forEach(file => {
        formData.append('files', file);
      });

      // Get auth token
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ['api://lgl-tools-api/access_as_user'],
        account: accounts[0]
      });

      // Upload with progress tracking
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(progress);
        }
      });

      const uploadPromise = new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            reject(new Error(xhr.responseText || 'Upload failed'));
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
      });

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';
      xhr.open('POST', `${apiUrl}/bom-extraction/upload`);
      xhr.setRequestHeader('Authorization', `Bearer ${tokenResponse.accessToken}`);
      xhr.send(formData);

      const result = await uploadPromise;

      if (result.success) {
        setFiles([]);
        onUploadComplete(result);
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload files');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div style={styles.container}>
      <div
        style={{
          ...styles.dropZone,
          ...(dragActive ? styles.dropZoneActive : {}),
          ...(disabled ? styles.dropZoneDisabled : {})
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={disabled ? undefined : handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          disabled={disabled}
        />
        
        <div style={styles.dropIcon}>📄</div>
        <h3 style={styles.dropTitle}>
          {dragActive ? 'Drop PDFs here' : 'Drag & drop PDF files here'}
        </h3>
        <p style={styles.dropSubtitle}>
          or click to browse • Max 50 files, 100MB each
        </p>
      </div>

      {error && (
        <div style={styles.error}>
          <span style={styles.errorIcon}>⚠️</span>
          <pre style={styles.errorText}>{error}</pre>
        </div>
      )}

      {files.length > 0 && (
        <div style={styles.fileList}>
          <div style={styles.fileListHeader}>
            <h4 style={styles.fileListTitle}>
              Selected Files ({files.length})
            </h4>
            <button
              style={styles.clearButton}
              onClick={() => setFiles([])}
            >
              Clear All
            </button>
          </div>
          
          <div style={styles.files}>
            {files.map((file, index) => (
              <div key={index} style={styles.fileItem}>
                <span style={styles.fileIcon}>📎</span>
                <div style={styles.fileInfo}>
                  <span style={styles.fileName}>{file.name}</span>
                  <span style={styles.fileSize}>{formatFileSize(file.size)}</span>
                </div>
                <button
                  style={styles.removeButton}
                  onClick={() => removeFile(index)}
                  title="Remove file"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          <div style={styles.uploadSection}>
            {uploading && (
              <div style={styles.progressBar}>
                <div 
                  style={{
                    ...styles.progressFill,
                    width: `${uploadProgress}%`
                  }}
                />
              </div>
            )}
            
            <button
              style={{
                ...styles.uploadButton,
                ...(uploading || disabled ? styles.uploadButtonDisabled : {})
              }}
              onClick={handleUpload}
              disabled={uploading || disabled}
            >
              {uploading ? `Uploading... ${uploadProgress}%` : `Upload ${files.length} File(s)`}
            </button>
          </div>
        </div>
      )}

      {!jobNumber && (
        <div style={styles.warning}>
          <span style={styles.warningIcon}>ℹ️</span>
          No job number set. Please navigate to this page from a project context.
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  dropZone: {
    border: '2px dashed #ddd',
    borderRadius: '12px',
    padding: '48px 24px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
    backgroundColor: '#fafafa'
  },
  dropZoneActive: {
    borderColor: '#007bff',
    backgroundColor: '#f0f7ff'
  },
  dropZoneDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  dropIcon: {
    fontSize: '48px',
    marginBottom: '16px'
  },
  dropTitle: {
    margin: '0 0 8px 0',
    fontSize: '18px',
    fontWeight: '600',
    color: '#333'
  },
  dropSubtitle: {
    margin: 0,
    color: '#666',
    fontSize: '14px'
  },
  error: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: '#fee',
    border: '1px solid #fcc',
    borderRadius: '8px'
  },
  errorIcon: {
    fontSize: '16px'
  },
  errorText: {
    margin: 0,
    fontSize: '13px',
    color: '#c00',
    whiteSpace: 'pre-wrap',
    fontFamily: 'inherit'
  },
  fileList: {
    border: '1px solid #eee',
    borderRadius: '12px',
    overflow: 'hidden'
  },
  fileListHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #eee'
  },
  fileListTitle: {
    margin: 0,
    fontSize: '14px',
    fontWeight: '600'
  },
  clearButton: {
    padding: '4px 12px',
    backgroundColor: 'transparent',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer'
  },
  files: {
    maxHeight: '300px',
    overflow: 'auto'
  },
  fileItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderBottom: '1px solid #f0f0f0'
  },
  fileIcon: {
    fontSize: '20px'
  },
  fileInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  fileName: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
    wordBreak: 'break-all'
  },
  fileSize: {
    fontSize: '12px',
    color: '#888'
  },
  removeButton: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: '#f0f0f0',
    cursor: 'pointer',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s'
  },
  uploadSection: {
    padding: '16px',
    backgroundColor: '#f8f9fa'
  },
  progressBar: {
    height: '6px',
    backgroundColor: '#e0e0e0',
    borderRadius: '3px',
    marginBottom: '12px',
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#007bff',
    borderRadius: '3px',
    transition: 'width 0.2s'
  },
  uploadButton: {
    width: '100%',
    padding: '12px 24px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  uploadButtonDisabled: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed'
  },
  warning: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: '#fff3cd',
    border: '1px solid #ffc107',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#856404'
  },
  warningIcon: {
    fontSize: '16px'
  }
};

export default PdfUploader;

